import { SiteContentService } from '@core/services';
import { mergeSiteContent } from '@core/services/site-content/site-content.service';
import { SiteContent } from '@core/services/site-content/site-content.types';
import { Component, DefineDependencies } from '@rhtml/component';
import { Container } from '@rxdi/core';
import { css, html, LitElement } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { StudioStyles } from '@shared/styles/studio.styles';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Logo } from '../navbar/logo';

interface FooterLink {
  label: string;
  href: string;
}

/** Footer columns; the contact details come from the editable content. */
const columns = (c: SiteContent): { title: string; links: FooterLink[] }[] => [
  {
    title: 'Work',
    links: c.work.cases.filter((w) => w.url).map((w) => ({ label: w.client.split('·')[0].trim(), href: w.url })),
  },
  {
    title: 'Open source',
    links: [
      { label: 'GitHub', href: c.openSource.githubUrl },
      ...c.openSource.packages
        .slice(0, 2)
        .map((p) => ({ label: p.name, href: `https://www.npmjs.com/package/${p.name}` })),
    ],
  },
  {
    title: 'Studio',
    links: [
      { label: 'Journal', href: '/blog' },
      { label: c.contact.email, href: `mailto:${c.contact.email}` },
      { label: c.contact.phone, href: `tel:${c.contact.phone.replace(/[^+\d]/g, '')}` },
    ],
  },
];

/** The visual site footer (the admin toolbox stays in <footer-component>). */
@Component({
  Settings: {
    selector: 'rs-site-footer',
    styles: [
      StudioStyles,
      css`
        :host {
          --primary-color: var(--rs-signal);
        }

        footer {
          position: relative;
          overflow: hidden;
          padding: clamp(56px, 8vw, 96px) 0 32px;
          border-top: 1px solid var(--rs-line);
          background: var(--rs-ink);
        }

        .top {
          display: grid;
          gap: 40px;
        }

        @media (min-width: 900px) {
          .top {
            grid-template-columns: 1.3fr repeat(3, 1fr);
          }
        }

        .brand {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          font-family: var(--rs-font-display);
          font-size: 20px;
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .brand svg {
          width: 30px;
          height: 34px;
        }

        .tagline {
          margin: 16px 0 0;
          max-width: 34ch;
          color: var(--rs-text-muted);
          line-height: 1.6;
        }

        h4 {
          margin: 0 0 16px;
          font-family: var(--rs-font-mono);
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--rs-text-muted);
        }

        ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          gap: 10px;
        }

        li a {
          color: var(--rs-text-2);
          transition: color 0.2s var(--rs-ease);
          word-break: break-word;
        }

        li a:hover {
          color: var(--rs-signal);
        }

        .wordmark {
          margin: clamp(48px, 8vw, 96px) 0 0;
          font-family: var(--rs-font-display);
          font-weight: 800;
          font-size: clamp(56px, 15vw, 220px);
          line-height: 0.8;
          letter-spacing: -0.06em;
          white-space: nowrap;
          color: transparent;
          -webkit-text-stroke: 1px var(--rs-text-muted);
          opacity: 0.45;
          user-select: none;
        }

        .legal {
          display: flex;
          justify-content: space-between;
          gap: 12px 24px;
          flex-wrap: wrap;
          margin-top: 28px;
          padding-top: 24px;
          border-top: 1px solid var(--rs-line);
          font-family: var(--rs-font-mono);
          font-size: 12px;
          color: var(--rs-text-muted);
        }

        .legal span + span::before {
          content: '·';
          margin-right: 12px;
        }

        .legal .ids {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        @media (max-width: 640px) {
          .legal,
          .legal .ids {
            flex-direction: column;
            gap: 6px;
          }
          .legal span + span::before {
            content: none;
          }
        }
      `,
    ],
  },
  Providers: DefineDependencies(SiteContentService)(Container),
  Loading: () => html``,
  State: function (this: SiteFooterComponent, [siteContent]) {
    return siteContent.content().pipe(catchError(() => of(mergeSiteContent([]))));
  },
  Render: () =>
    function (this: SiteFooterComponent, c: SiteContent) {
      return html`
        <footer>
          <div class="wrap">
            <div class="top">
              <div>
                <span class="brand">${Logo} Reactive Solutions</span>
                <p class="tagline">${c.company.tagline}</p>
              </div>
              ${columns(c).map(
                (col) => html`
                  <div>
                    <h4>${col.title}</h4>
                    <ul>
                      ${col.links.map(
                        (l) => html`<li>
                          <a
                            href=${l.href}
                            target=${l.href.startsWith('http') ? '_blank' : '_self'}
                            rel=${l.href.startsWith('http') ? 'noopener' : ''}
                            @click=${(e: Event) => this.follow(e, l.href)}
                            >${l.label}</a
                          >
                        </li>`
                      )}
                    </ul>
                  </div>
                `
              )}
            </div>

            <div class="wordmark" aria-hidden="true">reactive.</div>

            <div class="legal">
              <div class="ids">
                <span>&copy; ${c.company.founded}&ndash;${new Date().getFullYear()} ${c.company.name}</span>
                <span>VAT ${c.company.vat}</span>
                <span>${c.company.address}</span>
              </div>
              <span>Hosted on Lambforge</span>
            </div>
          </div>
        </footer>
      `;
    },
})
export class SiteFooterComponent extends LitElement {
  @Router() router: Router;

  /** In-app paths go through the router; external, mailto and tel links stay native. */
  private follow(event: Event, href: string) {
    if (!href.startsWith('/')) {
      return;
    }
    event.preventDefault();
    this.router.go(href);
    window.scrollTo({ top: 0 });
  }
}
